import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { CalendarBlank } from '@phosphor-icons/react';
import type { SiteContent } from '../../content/site';
import { paintThread } from './threads';

type Day = SiteContent['manifesto']['day'];

/** A block of the day. start and end are fractions of the day (0 = first hour, 1 = last). */
type Block = { label: string; start: number; end: number };

type DayPlan = { chores: Block[]; kept: Block[]; returned: Block[] };

/**
 * Lays the illustrative day out from the copy alone. Chores are grouped between the
 * kept blocks, and each group is the slot one returned block fills afterwards.
 * With the current copy (5 chores, 2 kept, 3 returned) every segment is two hours:
 * chores 8 h to 10 h, kept 10 h to 12 h, chores 12 h to 14 h, kept 14 h to 16 h,
 * chore 16 h to 18 h; the returned blocks fill 8 h to 10 h, 12 h to 14 h, 16 h to 18 h.
 */
function planDay(day: Day): DayPlan {
	const groups = Math.max(1, day.returned.length);
	const order: Array<{ kind: 'group' | 'kept'; index: number }> = [];
	for (let g = 0; g < groups; g += 1) {
		order.push({ kind: 'group', index: g });
		if (g < groups - 1 && g < day.kept.length) order.push({ kind: 'kept', index: g });
	}
	for (let k = Math.max(0, groups - 1); k < day.kept.length; k += 1) order.push({ kind: 'kept', index: k });

	const unit = 1 / order.length;
	const base = Math.floor(day.chores.length / groups);
	const extra = day.chores.length % groups;
	const plan: DayPlan = { chores: [], kept: [], returned: [] };
	let chore = 0;

	order.forEach((slot, position) => {
		const start = position * unit;
		const end = start + unit;
		if (slot.kind === 'kept') {
			plan.kept.push({ label: day.kept[slot.index], start, end });
			return;
		}
		if (day.returned[slot.index]) plan.returned.push({ label: day.returned[slot.index], start, end });
		const count = base + (slot.index < extra ? 1 : 0);
		for (let c = 0; c < count; c += 1) {
			plan.chores.push({ label: day.chores[chore], start: start + (c * unit) / count, end: start + ((c + 1) * unit) / count });
			chore += 1;
		}
	});

	return plan;
}

/** Lane geometry inside the flow area (px). */
const LANE_WIDTH = 28;
const LANE_RIGHT = 4;
const SPREAD = 4;

type Thread = { d: string };

/**
 * One thread per chore: it leaves the chore horizontally, bends down into the lane and
 * runs to the end of the day. Top chores take the outer strands, so threads never cross.
 */
function buildThreads(chores: Block[], width: number, height: number): Thread[] {
	// Before the first measure the paths still exist (empty), so timelines can bind to them.
	if (!width || !height) return chores.map(() => ({ d: 'M 0 0' }));
	const center = width - LANE_RIGHT - LANE_WIDTH / 2;
	const end = height + 4;
	return chores.map((chore, index) => {
		const y = ((chore.start + chore.end) / 2) * height;
		const x0 = 8;
		const x1 = center + ((chores.length - 1) / 2 - index) * SPREAD;
		const drop = Math.max(10, Math.min(64, height - y - 6));
		return { d: `M ${x0} ${y} C ${x0 + (x1 - x0) * 0.62} ${y}, ${x1} ${y}, ${x1} ${y + drop} L ${x1} ${end}` };
	});
}

const place = (block: Block): CSSProperties => ({
	top: `calc(${block.start * 100}% + 2px)`,
	height: `calc(${(block.end - block.start) * 100}% - 4px)`,
});

const BLOCK = 'h-full overflow-hidden rounded-[10px] px-3 py-1.5 text-[13px] font-medium leading-[1.25]';

/** Warm gold for the time given back: Solar mixed into Mineral, Graphite text (12:1). */
const GOLD = 'bg-gradient-to-br from-[#F6EBC8] to-[#EFD995] text-graphite ring-1 ring-inset ring-solar/70';

/**
 * "Une équipe": a typical day as a calendar column. Chores (quiet Mist) leave the day
 * and flow as fine threads into one thin automation lane, where they keep running
 * (each thread still starts at its hour, the solar tips rest at the end of the day);
 * the freed slots fill with warm gold (time given back); the human work (Forest) stays.
 *
 * Default markup = final state (chores gone, threads in the lane, gold blocks, both
 * legends). The section's timelines replay the shift (dayShift in motion.ts).
 * The diagram is aria-hidden; assistive tech gets the same facts as two short lists.
 *
 * fill: the calendar takes the remaining height of a fixed stage (pinned scene);
 * otherwise it has its own height.
 */
export function DayTimeline({ day, fill = false }: { day: Day; fill?: boolean }) {
	const plan = useMemo(() => planDay(day), [day]);
	const flow = useRef<HTMLDivElement>(null);
	const [size, setSize] = useState({ width: 0, height: 0 });
	const lastChore = plan.chores.length - 1;

	useLayoutEffect(() => {
		const el = flow.current;
		if (!el) return;
		const measure = () =>
			setSize((prev) =>
				prev.width === el.clientWidth && prev.height === el.clientHeight
					? prev
					: { width: el.clientWidth, height: el.clientHeight },
			);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const threads = useMemo(() => buildThreads(plan.chores, size.width, size.height), [plan, size]);

	// New geometry: repaint each thread's current window in pixels of its new length.
	useLayoutEffect(() => {
		flow.current?.querySelectorAll<SVGPathElement>('[data-day-thread]').forEach(paintThread);
	}, [threads]);

	return (
		<figure className={`mx-auto flex w-full max-w-[36rem] flex-col ${fill ? 'h-full' : ''}`}>
			<figcaption className="flex items-center gap-2 text-[13px] font-medium leading-none text-graphite-muted dark:text-dusk-muted">
				<CalendarBlank aria-hidden size={15} weight="bold" className="text-forest dark:text-sage" />
				{day.label}
			</figcaption>

			<div aria-hidden className={`relative mt-7 ${fill ? 'min-h-0 flex-1' : 'h-[30rem] sm:h-[28rem]'}`}>
				{/* Hour scale: labels in the gutter, hairlines across the day column only. */}
				{day.hours.map((hour, index) => {
					const top = `${(index / Math.max(1, day.hours.length - 1)) * 100}%`;
					return (
						<div key={hour} className="absolute left-0 w-full" style={{ top }}>
							<span className="absolute left-0 -translate-y-1/2 text-[12px] font-medium tabular-nums leading-none text-graphite-muted dark:text-dusk-muted">
								{hour}
							</span>
							<span className="absolute left-11 h-px w-[calc(68%-2.75rem)] bg-graphite/[0.09] sm:w-[calc(70%-2.75rem)] dark:bg-mineral/10" />
						</div>
					);
				})}

				{/* The day column. */}
				<div className="absolute inset-y-0 left-11 w-[calc(68%-2.75rem)] sm:w-[calc(70%-2.75rem)]">
					{plan.kept.map((block) => (
						<div key={`kept-${block.label}`} data-day-kept className="absolute inset-x-0" style={place(block)}>
							<div className={`${BLOCK} bg-forest text-mineral dark:ring-1 dark:ring-inset dark:ring-mineral/10`}>{block.label}</div>
						</div>
					))}
					{plan.returned.map((block) => (
						<div key={`returned-${block.label}`} data-day-returned className="absolute inset-x-0" style={place(block)}>
							<div className={`${BLOCK} ${GOLD}`}>{block.label}</div>
						</div>
					))}
					{plan.chores.map((block, index) => (
						<div
							key={`chore-${block.label}`}
							data-day-chore
							data-day-target={index === lastChore ? '' : undefined}
							className="absolute inset-x-0 opacity-0"
							style={place(block)}
						>
							<div className={`${BLOCK} bg-mist text-graphite-muted ring-1 ring-inset ring-graphite/[0.05] dark:bg-forest-deep dark:text-dusk-muted dark:ring-mineral/10`}>
								{block.label}
							</div>
							{index === lastChore && (
								<span
									data-day-focus
									className="pointer-events-none absolute -inset-[3px] rounded-[13px] border-2 border-solar opacity-0"
								/>
							)}
						</div>
					))}
				</div>

				{/* The automation lane and the threads flowing into it. */}
				<div ref={flow} data-day-flow className="absolute inset-y-0 left-[68%] right-0 sm:left-[70%]">
					<span
						className="absolute -bottom-2 top-0 rounded-full bg-mist/80 ring-1 ring-inset ring-graphite/[0.06] dark:bg-forest-deep dark:ring-mineral/10"
						style={{ right: LANE_RIGHT, width: LANE_WIDTH }}
					/>
					<svg
						className="absolute inset-0 h-full w-full overflow-visible"
						width={size.width || undefined}
						height={size.height || undefined}
						viewBox={`0 0 ${Math.max(1, size.width)} ${Math.max(1, size.height)}`}
						fill="none"
					>
						{threads.map((thread, index) => (
							<g key={index}>
								<path
									data-day-thread
									data-a="0"
									data-b="1"
									d={thread.d}
									className="stroke-forest/[0.55] dark:stroke-sage/[0.6]"
									strokeWidth={1.25}
									strokeLinecap="round"
								/>
								<path d={thread.d} stroke="#E8C96A" strokeWidth={2.5} strokeLinecap="round" />
							</g>
						))}
					</svg>
				</div>
			</div>

			<div aria-hidden className="mt-7 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
				<p data-day-legend="returned" className="flex items-center gap-2 text-[13px] font-medium leading-none text-graphite dark:text-mineral">
					<span className={`h-3 w-3 shrink-0 rounded-[4px] ${GOLD}`} />
					{day.returnedLabel}
				</p>
				<p
					data-day-legend="absorbed"
					className="ml-auto flex max-w-[17rem] items-center justify-end gap-2.5 text-right text-[13px] font-medium leading-snug text-graphite-muted dark:text-dusk-muted"
				>
					<svg width="22" height="10" viewBox="0 0 22 10" fill="none" className="shrink-0">
						<path
							d="M1 2 C 9 2, 13 2, 13 6 L 13 8"
							className="stroke-forest/[0.55] dark:stroke-sage/[0.6]"
							strokeWidth="1.25"
							strokeLinecap="round"
						/>
						<path d="M13 7 L 13 8.6" stroke="#E8C96A" strokeWidth="2.5" strokeLinecap="round" />
					</svg>
					{day.absorbedLabel}
				</p>
			</div>

			<div className="sr-only">
				<p>{day.absorbedLabel}</p>
				<ul>
					{day.chores.map((chore) => (
						<li key={chore}>{chore}</li>
					))}
				</ul>
				<p>{day.returnedLabel}</p>
				<ul>
					{day.returned.map((item) => (
						<li key={item}>{item}</li>
					))}
				</ul>
			</div>
		</figure>
	);
}
